//@ts-nocheck
import { brokerFac } from '../../main';
//OI Profile Constants and Helper Functions
export const OI_PANEL_WIDTH_RATIO = 0.25; // 25% of chart width
export const BAR_HEIGHT = 10; // height of each bar (call or put)
export const BAR_GAP = 0; // no gap between call and put bars
export const CALL_COLOR = "rgba(209, 103, 119, 0.6)"; // light transparent
export const PUT_COLOR = "rgba(52, 173, 160, 0.6)";
export const LABEL_COLOR = "#eee";
export const OI_PROFILE_STORAGE_KEY = "oiProfileEnabled"; //OI Profile toggle state
//Parses expiry from the Name
export function parseExpiryFromName(name) {
    const match = name === null || name === void 0 ? void 0 : name.match(/^(.*?)\s+(\d{1,2})\s+([A-Z]{3})\s+(\d{4})/);
    if (!match)
        return "";
    const [, prefix, day, month, year] = match;
    const shortYear = year.slice(-2);
    return `${prefix} ${day} ${month}'${shortYear}`;
}
/**
 * Transforms raw option chain API response into { price, call, put, expiry } format.
 * Each pair of CE/PE entries sharing the same StrikeRate is merged into one row.
 */
export function transformOiData(rawData) {
    const strikeMap = new Map();
    for (const item of rawData) {
        const strike = item.StrikeRate;
        if (!strikeMap.has(strike)) {
            strikeMap.set(strike, { call: 0, put: 0, expiry: parseExpiryFromName(item.Name || ""), ceItem: null, peItem: null });
        }
        const entry = strikeMap.get(strike);
        if (item.CPType === "CE") {
            entry.call = item.OpenInterest;
            entry.ceItem = item;
        }
        else if (item.CPType === "PE") {
            entry.put = item.OpenInterest;
            entry.peItem = item;
        }
        if (!entry.expiry && item.Name) {
            entry.expiry = parseExpiryFromName(item.Name);
        }
    }
    return Array.from(strikeMap.entries())
        .sort(([a], [b]) => a - b)
        .map(([price, { call, put, expiry, ceItem, peItem }]) => ({ price, call, put, expiry, ceItem, peItem }));
}
/**
 * Filters an OptionChainList to only include strikes within ± strikesCount
 * of the ATM strike (the strike closest to ltp).
 *
 * @param optionChainList  Raw OptionChainList array from the API
 * @param ltp              Current LTP (from Lastrate[0].LTP)
 * @param strikesCount     Number of strikes above AND below ATM to keep (default 20)
 * @returns Filtered OptionChainList containing at most (2 * strikesCount + 1) unique strikes
 */
export function filterOptionChainByLtp(optionChainList, ltp, strikesCount = 20) {
    if (!optionChainList || optionChainList.length === 0 || ltp == null)
        return optionChainList;
    // Get unique sorted strike rates
    const uniqueStrikes = Array.from(new Set(optionChainList.map((item) => item.StrikeRate))).sort((a, b) => a - b);
    // Find ATM index — strike closest to LTP
    let atmIndex = 0;
    let minDiff = Infinity;
    for (let i = 0; i < uniqueStrikes.length; i++) {
        const diff = Math.abs(uniqueStrikes[i] - ltp);
        if (diff < minDiff) {
            minDiff = diff;
            atmIndex = i;
        }
    }
    // Slice ±strikesCount around ATM
    const fromIndex = Math.max(0, atmIndex - strikesCount);
    const toIndex = Math.min(uniqueStrikes.length - 1, atmIndex + strikesCount);
    const allowedStrikes = new Set(uniqueStrikes.slice(fromIndex, toIndex + 1));
    return optionChainList.filter((item) => allowedStrikes.has(item.StrikeRate));
}
/**
 * Converts a price value to a Y pixel coordinate on the canvas.
 * Higher price = lower Y (top of canvas).
 */
export function priceToY(price, minPrice, maxPrice, canvasHeight) {
    const ratio = (maxPrice - price) / (maxPrice - minPrice);
    return ratio * canvasHeight;
}
//OI Drawing main function
/**
 * Draws the OI Profile bars on the given canvas context.
 * @param ctx - canvas 2D rendering context
 * @param canvas - the canvas element
 * @param oiData - array of { price, call, put, expiry } strike data
 * @param currentPriceRange - visible price range from TradingView API
 */
/**
 * Computes a dynamic bar height based on the visible price range and canvas size.
 * When zoomed in (small price range) → larger bars.
 * When zoomed out (large price range) → smaller bars.
 *
 * @param oiData          - array of strike data
 * @param currentPriceRange - visible price range
 * @param canvasHeight    - canvas height in pixels
 * @returns bar height in pixels (clamped between 3 and 20)
 */
export function computeBarHeight(oiData, currentPriceRange, canvasHeight) {
    const MIN_BAR = 3;
    const MAX_BAR = 20;
    if (oiData.length === 0 || canvasHeight <= 0)
        return BAR_HEIGHT;
    const allPrices = oiData.map((d) => d.price);
    const minPrice = currentPriceRange
        ? Math.min(currentPriceRange.from, currentPriceRange.to)
        : Math.min(...allPrices);
    const maxPrice = currentPriceRange
        ? Math.max(currentPriceRange.from, currentPriceRange.to)
        : Math.max(...allPrices);
    // Count strikes visible in the current price range
    const visibleStrikes = oiData.filter((d) => d.price >= minPrice && d.price <= maxPrice);
    const count = visibleStrikes.length;
    if (count <= 1)
        return MAX_BAR;
    // Pixels available per strike (both call + put bars share this space)
    const pixelsPerStrike = canvasHeight / count;
    // Each bar takes ~40% of the per-strike space
    const computed = Math.floor(pixelsPerStrike * 0.4);
    return Math.max(MIN_BAR, Math.min(MAX_BAR, computed));
}
export function drawOIProfile(ctx, canvas, oiData, currentPriceRange) {
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    const allPrices = oiData.map((d) => d.price);
    const minPrice = currentPriceRange
        ? Math.min(currentPriceRange.from, currentPriceRange.to)
        : Math.min(...allPrices);
    const maxPrice = currentPriceRange
        ? Math.max(currentPriceRange.from, currentPriceRange.to)
        : Math.max(...allPrices);
    const priceRange = maxPrice - minPrice;
    if (priceRange <= 0)
        return;
    const maxOI = Math.max(...oiData.map((d) => Math.max(d.call, d.put)));
    if (maxOI === 0)
        return;
    // Dynamic bar height — scales with zoom level
    const dynamicBarHeight = computeBarHeight(oiData, currentPriceRange, H);
    const axisX = W;
    const maxBarW = W - 4;
    oiData.forEach((d) => {
        const centerY = priceToY(d.price, minPrice, maxPrice, H);
        if (centerY < -20 || centerY > H + 20)
            return;
        const callW = (d.call / maxOI) * maxBarW;
        const putW = (d.put / maxOI) * maxBarW;
        // CALL bar — above center line
        ctx.fillStyle = CALL_COLOR;
        ctx.fillRect(axisX - callW, centerY - dynamicBarHeight, callW, dynamicBarHeight);
        // PUT bar — below center line
        ctx.fillStyle = PUT_COLOR;
        ctx.fillRect(axisX - putW, centerY, putW, dynamicBarHeight);
    });
}
/**
 * Parses a TradingView symbol string like "NSE:TCS/11536-C"
 * into { symbol, exch, exchType }.
 */
export function parseSymbolString(symbolStr) {
    try {
        const [exchPart, rest] = symbolStr.split(":");
        if (!rest)
            return null;
        const namePart = rest.split("/")[0];
        const exchTypePart = rest.split("-")[1];
        if (!namePart || !exchTypePart)
            return null;
        return { symbol: namePart.trim(), exch: exchPart.trim(), exchType: exchTypePart.trim() };
    }
    catch (_a) {
        return null;
    }
}
// isoption symbol 
export function isOptionSymbol(symbolStr) {
    try {
        const [, rest] = symbolStr.split(":");
        if (!rest)
            return false;
        const namePart = rest.split("/")[0];
        // Match " CE " or " PE " followed by a numeric strike price
        return /\s+(CE|PE)\s+\d/i.test(namePart);
    }
    catch (_a) {
        return false;
    }
}
const MONTH_MAP = {
    JAN: "01", FEB: "02", MAR: "03", APR: "04",
    MAY: "05", JUN: "06", JUL: "07", AUG: "08",
    SEP: "09", OCT: "10", NOV: "11", DEC: "12",
};
/**
 * Parses a plain futures name (from ScripInfoDetails.Name) that contains an expiry date
 * but has NO exchange prefix and NO "/scrip-exchType" suffix.
 * Examples:
 *   "NIFTY 30 Jun 2026"   →  { underlying: "NIFTY", expiryDate: "2026-06-30" }
 *   "BANKNIFTY 29 MAY 2026" → { underlying: "BANKNIFTY", expiryDate: "2026-05-29" }
 *
 * Returns null if the name does not match the expected pattern.
 */
export function parsePlainExpiryName(name) {
    try {
        const dateMatch = name.trim().match(/^(.*?)\s+(\d{1,2})\s+([A-Z]{3})\s+(\d{4})$/i);
        if (!dateMatch)
            return null;
        const [, underlying, day, mon, year] = dateMatch;
        const monthNum = MONTH_MAP[mon.toUpperCase()];
        if (!monthNum)
            return null;
        const expiryDate = `${year}-${monthNum}-${day.padStart(2, "0")}`;
        return { underlying: underlying.trim(), expiryDate };
    }
    catch (_a) {
        return null;
    }
}
/**
 * Parses an index/futures symbol that contains an explicit expiry date.
 * Examples:
 *   "NSE:NIFTY 12 MAY 2026/66071-D"  →  { underlying: "NIFTY", expiryDate: "2026-05-12", exch: "N", exchType: "D" }
 *   "NSE:BANKNIFTY 29 MAY 2026/12345-D" → { underlying: "BANKNIFTY", expiryDate: "2026-05-29", exch: "N", exchType: "D" }
 *
 * Returns null if the symbol does not match the expected pattern.
 */
export function parseExpirySymbol(symbolStr) {
    var _a;
    try {
        const [exchPart, rest] = symbolStr.split(":");
        if (!rest)
            return null;
        const slashIdx = rest.indexOf("/");
        if (slashIdx === -1)
            return null;
        const namePart = rest.slice(0, slashIdx).trim(); // e.g. "NIFTY 12 MAY 2026"
        const afterSlash = rest.slice(slashIdx + 1); // e.g. "66071-D"
        const exchType = afterSlash.split("-")[1];
        if (!exchType)
            return null;
        // Match: <UNDERLYING> <DD> <MON> <YYYY>
        // The underlying may contain spaces (e.g. "NIFTY 50") so we match greedily up to the date
        const dateMatch = namePart.match(/^(.*?)\s+(\d{1,2})\s+([A-Z]{3})\s+(\d{4})$/i);
        if (!dateMatch)
            return null;
        const [, underlying, day, mon, year] = dateMatch;
        const monthNum = MONTH_MAP[mon.toUpperCase()];
        if (!monthNum)
            return null;
        const expiryDate = `${year}-${monthNum}-${day.padStart(2, "0")}`;
        // Map exchange prefix to single-char code used by the API
        const exchMap = { NSE: "N", BSE: "B", MCX: "M" };
        const exch = (_a = exchMap[exchPart.toUpperCase()]) !== null && _a !== void 0 ? _a : exchPart[0];
        return { underlying: underlying.trim(), expiryDate, exch, exchType: exchType.trim() };
    }
    catch (_b) {
        return null;
    }
}
// OI PROFILE TOGGLE HELPERS
/**
 * Show or hide all OI Profile canvases tracked in the provided list.
 * @param oiCanvasList - array of canvas elements to show/hide
 * @param visible - true to show, false to hide
 */
export function setOIProfileVisibility(oiCanvasList, visible) {
    oiCanvasList.forEach((c) => {
        c.style.display = visible ? "block" : "none";
    });
}
// OI Profile Button 
export function updateOIProfileButton(oiCheckbox, enabled, themeVal) {
    oiCheckbox.classList.remove("enabled", "disabled", "dark", "light");
    if (enabled) {
        oiCheckbox.classList.add("enabled");
    }
    else {
        oiCheckbox.classList.add("disabled");
        oiCheckbox.classList.add(themeVal === "dark" ? "dark" : "light");
    }
}
//Format OI Values to show in tooltip
export const formatOI = (value) => {
    const format = (num) => Number(num.toFixed(2)).toString();
    if (value >= 10000000) {
        return format(value / 10000000) + " Cr";
    }
    else if (value >= 100000) {
        return format(value / 100000) + " L";
    }
    else if (value >= 1000) {
        return format(value / 1000) + " K";
    }
    else {
        return value.toString();
    }
};
//OI WEBSOCKET UPDATE HANDLER
export function createOiWebsocketUpdateHandler(oiDataMap) {
    return function handleOiWebsocketUpdate(websocketOiData) {
        if (!websocketOiData || websocketOiData.Token == null || websocketOiData.OpenInterest == null)
            return;
        const token = Number(websocketOiData.Token);
        const newOI = Number(websocketOiData.OpenInterest);
        oiDataMap.forEach((strikeList) => {
            for (const strike of strikeList) {
                if (strike.ceItem && Number(strike.ceItem.ScripCode) === token) {
                    strike.call = newOI;
                    strike.ceItem = { ...strike.ceItem, OpenInterest: newOI };
                    return;
                }
                if (strike.peItem && Number(strike.peItem.ScripCode) === token) {
                    strike.put = newOI;
                    strike.peItem = { ...strike.peItem, OpenInterest: newOI };
                    return;
                }
            }
        });
    };
}
//FETCH OI(API) DATA FOR PANE
/**
 * Creates a fetchOiDataForPane function bound to the provided dependencies.
 */
export function createFetchOiDataForPane(deps) {
    const { oiDataMap, ScripInfoDetails, getOiProfileData, subscribeToData, SOCKET_SUBSCRIPTION_METHODS, METHOD_FOR_CHART_SOCKET, handleOiWebsocketUpdate, } = deps;
    return async function fetchOiDataForPane(paneIndex, symbolStr, _retryCount = 0) {
        var _a, _b, _c, _d;
        try {
            // ── Skip individual option contracts (CE / PE) — no OI chain data available ──
            if (symbolStr && isOptionSymbol(symbolStr)) {
                oiDataMap.set(paneIndex, []);
                return;
            }
            let rawApiData;
            // ── Check if the symbol contains an explicit expiry date (e.g. "NSE:NIFTY 12 MAY 2026/66071-D") ──
            const expiryParsed = symbolStr ? parseExpirySymbol(symbolStr) : null;
            if (expiryParsed) {
                // Use the V3 API that accepts an expiry date
                const { underlying, expiryDate, exch, exchType } = expiryParsed;
                rawApiData = await getOiProfileData(underlying, exch, exchType); //await getOiProfileDataV3(underlying, exch, exchType, expiryDate);
            }
            else {
                // Standard symbol (e.g. "NSE:TCS/11536-C") — use the existing API
                let sym, exch, exchType;
                if (symbolStr) {
                    const parsed = parseSymbolString(symbolStr);
                    if (!parsed)
                        return;
                    sym = parsed.symbol;
                    exch = parsed.exch;
                    exchType = parsed.exchType;
                }
                else {
                    try {
                        const activeChartSymbol = window.tvWidget.activeChart().symbol();
                        const parsed = parseSymbolString(activeChartSymbol);
                        sym = parsed.symbol;
                        exch = parsed.exch;
                        exchType = parsed.exchType;
                    }
                    catch (err) {
                        console.warn("Failed to parse symbol from TradingView API, falling back to ScripInfoDetails", err);
                        sym = (ScripInfoDetails === null || ScripInfoDetails === void 0 ? void 0 : ScripInfoDetails.Name) || (ScripInfoDetails === null || ScripInfoDetails === void 0 ? void 0 : ScripInfoDetails.ShortName);
                        exch = ScripInfoDetails === null || ScripInfoDetails === void 0 ? void 0 : ScripInfoDetails.Exch;
                        exchType = ScripInfoDetails === null || ScripInfoDetails === void 0 ? void 0 : ScripInfoDetails.ExchType;
                    }
                }
                if (!sym || !exch || !exchType)
                    return;
                // ── Check if the plain name from ScripInfoDetails contains an expiry date ──
                // e.g. "NIFTY 30 Jun 2026" → use V3 API with the parsed expiry date
                const plainExpiry = parsePlainExpiryName(sym);
                if (plainExpiry) {
                    const { underlying, expiryDate } = plainExpiry;
                    rawApiData = await getOiProfileData(underlying, exch[0], exchType); //await getOiProfileDataV3(underlying, exch[0], exchType, expiryDate);
                }
                else {
                    rawApiData = await getOiProfileData(sym, exch[0], exchType);
                }
            }
            // V3 API returns "Options"; legacy API returns "OptionChainList"
            const rawList = Array.isArray(rawApiData.Options)
                ? rawApiData.Options
                : Array.isArray(rawApiData.OptionChainList)
                    ? rawApiData.OptionChainList
                    : ((_a = rawApiData === null || rawApiData === void 0 ? void 0 : rawApiData.OptionChainList) !== null && _a !== void 0 ? _a : []);
            const ltp = (_d = (_c = (_b = rawApiData === null || rawApiData === void 0 ? void 0 : rawApiData.Lastrate) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.LTP) !== null && _d !== void 0 ? _d : null;
            const list = ltp != null ? filterOptionChainByLtp(rawList, ltp, 20) : rawList;
            oiDataMap.set(paneIndex, list.length > 0 ? transformOiData(list) : []);
            const logSym = expiryParsed ? `${expiryParsed.underlying} (${expiryParsed.expiryDate})` : symbolStr !== null && symbolStr !== void 0 ? symbolStr : "default";
            if (list.length > 0) {
                const marketFeedData = list.map((item) => ({
                    Exch: item.EXCH || item.Exch || "N",
                    ScripCode: Number(item.ScripCode),
                    ExchType: item.ExchType || "D",
                }));
                const subscriberKey = `oi_profile_pane_${paneIndex}`;
                subscribeToData({
                    Method: SOCKET_SUBSCRIPTION_METHODS.SCRIPT_INFO_FOR_FUTURE,
                    Operation: METHOD_FOR_CHART_SOCKET,
                    MarketFeedData: marketFeedData,
                }, subscriberKey, handleOiWebsocketUpdate);
            }
        }
        catch (err) {
            console.error(`OI Profile [pane ${paneIndex}]: failed to load option chain data`, err);
            // Retry up to 3 times with exponential back-off (2s, 4s, 8s)
            const MAX_RETRIES = 3;
            if (_retryCount < MAX_RETRIES) {
                const delay = Math.pow(2, _retryCount + 1) * 1000; // 2s, 4s, 8s
                setTimeout(() => fetchOiDataForPane(paneIndex, symbolStr, _retryCount + 1), delay);
            }
            else {
                // All retries exhausted — clear data so bars disappear rather than showing stale data
                oiDataMap.set(paneIndex, []);
            }
        }
    };
}
//CANVAS INJECTION & TOOLTIP SETUP
/**
 * Initialises the OI Profile canvas injection loop.
 * Call this once after the TradingView iframe is ready.
 *
 * @param config - all external dependencies needed by the canvas/tooltip logic
 */
export function initOIProfileCanvas(config) {
    const { iframe, oiDataMap, oiCanvasList, priceRangeMap, getOiProfileEnabled, getTheme, fetchOiDataForPane, tvWidget, unsubscribeDataBySubscriber, } = config;
    // Shared hide-delay timer for OI tooltip
    let oiTooltipHideTimer = null;
    // Re-fetch OI data for all active panes when the network reconnects
    window.addEventListener("online", () => {
        setTimeout(() => {
            try {
                const count = tvWidget.chartsCount();
                for (let i = 0; i < count; i++) {
                    try {
                        const sym = tvWidget.chart(i).symbol();
                        fetchOiDataForPane(i, sym || undefined);
                    }
                    catch (_) { }
                }
            }
            catch (_) { }
        }, 2000);
    });
    setInterval(() => {
        //finding Tradingview Canvas
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        const paneDiv = iframeDoc.querySelector(".layout__area--center");
        if (!paneDiv)
            return;
        const chartContainers = paneDiv.querySelectorAll(".chart-container");
        if (chartContainers.length === 0)
            return;
        // ── Inject OI tooltip button hover styles (once, in iframe doc)
        if (!iframeDoc.getElementById("oi-tooltip-styles")) {
            const styleEl = iframeDoc.createElement("style");
            styleEl.id = "oi-tooltip-styles";
            styleEl.textContent = `
        #oi-tooltip button.oi-buy,
        #oi-tooltip button.oi-sell {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 2px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
          font-family: inherit;
          padding: 0;
          pointer-events: auto;
          line-height: 1;
          transition: background-color 0.15s, color 0.15s, border-color 0.15s;
        }
        #oi-tooltip button.oi-buy {
          background-color: rgba(0, 178, 158, 0.15);
          border: 1px solid rgba(0, 178, 158, 0.15);
          color: #00b29e;
        }
        #oi-tooltip button.oi-buy:hover {
          background-color: #00b29e;
          border-color: #039855;
          color: #fff;
        }
        #oi-tooltip button.oi-sell {
          background-color: rgba(229, 69, 93, 0.15);
          border: 1px solid rgba(229, 69, 93, 0.15);
          color: #E31B54;
        }
        #oi-tooltip button.oi-sell:hover {
          background-color: #E31B54;
          border-color: #e5455d;
          color: #fff;
        }
      `;
            iframeDoc.head.appendChild(styleEl);
        }
        // ── Create shared tooltip div (once, in iframe doc)
        let oiTooltip = iframeDoc.getElementById("oi-tooltip");
        if (!oiTooltip) {
            oiTooltip = iframeDoc.createElement("div");
            oiTooltip.id = "oi-tooltip";
            Object.assign(oiTooltip.style, {
                position: "fixed",
                display: "none",
                background: "#1e1e2e",
                border: "1px solid #555",
                borderRadius: "6px",
                padding: "7px 10px",
                color: "#eee",
                fontSize: "12px",
                fontFamily: "Arial, sans-serif",
                // zIndex:          "99999",
                pointerEvents: "auto",
                boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
                minWidth: "200px",
                lineHeight: "1.25",
            });
            iframeDoc.body.appendChild(oiTooltip);
        }
        //function to use hide the tooltip 
        const scheduleTooltipHide = () => {
            if (oiTooltipHideTimer)
                clearTimeout(oiTooltipHideTimer);
            oiTooltipHideTimer = setTimeout(() => {
                if (oiTooltip)
                    oiTooltip.style.display = "none";
            }, 150);
        };
        const cancelTooltipHide = () => {
            if (oiTooltipHideTimer) {
                clearTimeout(oiTooltipHideTimer);
                oiTooltipHideTimer = null;
            }
        };
        // Keep tooltip visible while mouse is over it
        if (!oiTooltip.dataset.eventsAttached) {
            oiTooltip.dataset.eventsAttached = "1";
            oiTooltip.addEventListener("mouseenter", cancelTooltipHide);
            oiTooltip.addEventListener("mouseleave", scheduleTooltipHide);
            // #IMP#── Tooltip button clicks: open Buy Form
            oiTooltip.addEventListener("click", (e) => {
                var _a;
                const btn = e.target.closest("button[data-action]");
                if (!btn)
                    return;
                const action = btn.dataset.action;
                const itemJson = btn.dataset.item;
                if (!itemJson)
                    return;
                try {
                    const item = JSON.parse(itemJson);
                    const exchMap = {
                        N: "NSE",
                        B: "BSE",
                        M: "MCX"
                    };
                    const exch = exchMap[(_a = item.EXCH) === null || _a === void 0 ? void 0 : _a.toUpperCase()] || item.EXCH;
                    const Symbol = `${exch}:${item.Name}/${item.ScripCode}-${item.ExchType}`; //ticker to open buy form
                    const prodType = localStorage.getItem("productVal");
                    const type = localStorage.getItem("defaultClose");
                    const host = brokerFac._handleMessageEvent;
                    const isBuy = (action === null || action === void 0 ? void 0 : action.startsWith("buy")) ? 1 : -1;
                    const orderData = {
                        symbol: Symbol,
                        side: isBuy,
                        type: type === "Limit" ? 1 : 2,
                        customFields: { id: prodType || "Delivery" },
                    };
                    (host === null || host === void 0 ? void 0 : host.showOrderDialog) ? host.showOrderDialog(orderData) : null;
                    //hide tooltip after click on buy/sell
                    setTimeout(() => { oiTooltip.style.display = "none"; }, 100);
                }
                catch (error) {
                    console.error("Error handling order:", error);
                }
            });
        }
        //Injection Canvas to all existing panes.
        chartContainers.forEach((container, domIndex) => {
            const guiWrapper = container.querySelector(".chart-gui-wrapper");
            if (!guiWrapper)
                return;
            // ── Assign a stable unique pane ID to each guiWrapper (survives DOM reorder) ──
            if (!guiWrapper.dataset.oiPaneId) {
                guiWrapper.dataset.oiPaneId = String(domIndex);
            }
            const paneId = Number(guiWrapper.dataset.oiPaneId);
            if (guiWrapper.querySelector(`[data-oi-canvas]`))
                return;
            guiWrapper.style.position = "relative";
            const canvas = iframeDoc.createElement("canvas");
            canvas.setAttribute("data-oi-canvas", `chart-${paneId}`);
            const setCanvasSize = () => {
                const w = guiWrapper.clientWidth * OI_PANEL_WIDTH_RATIO;
                const h = guiWrapper.clientHeight;
                if (w > 0 && h > 0) {
                    canvas.width = w;
                    canvas.height = h;
                }
            };
            setCanvasSize();
            Object.assign(canvas.style, {
                position: "absolute",
                top: "0",
                right: "0",
                width: `${OI_PANEL_WIDTH_RATIO * 100}%`,
                height: "100%",
                zIndex: "999",
                pointerEvents: "none",
                background: "transparent",
                borderLeft: "none",
            });
            guiWrapper.appendChild(canvas);
            canvas.style.display = getOiProfileEnabled() ? "block" : "none";
            oiCanvasList.push(canvas);
            const ctx = canvas.getContext("2d");
            // paneId equals the TradingView chart index (both are assigned from the initial DOM order,
            // which matches tvWidget.chart(i)). TV chart indices are stable — they do NOT change when
            // the user expands/collapses panes. We therefore use paneId directly for all tvWidget API
            // calls instead of re-resolving the DOM position (which DOES change on expand/collapse).
            priceRangeMap.set(paneId, null);
            try {
                const chartN = tvWidget.chart(paneId);
                const r = chartN.getPanes()[0].getMainSourcePriceScale().getVisiblePriceRange();
                if (r)
                    priceRangeMap.set(paneId, { from: r.from, to: r.to });
            }
            catch (_) { /* ignore if chart not ready */ }
            if (paneId > 0 && getOiProfileEnabled()) {
                try {
                    const sym = tvWidget.chart(paneId).symbol();
                    fetchOiDataForPane(paneId, sym);
                }
                catch (_) {
                    fetchOiDataForPane(paneId);
                }
            }
            try {
                tvWidget.chart(paneId).onSymbolChanged().subscribe(null, () => {
                    if (!getOiProfileEnabled())
                        return;
                    setTimeout(() => {
                        try {
                            const sym = tvWidget.chart(paneId).symbol();
                            fetchOiDataForPane(paneId, sym);
                        }
                        catch (_) { /* ignore */ }
                    }, 150);
                });
            }
            catch (_) { /* ignore */ }
            function getHoveredStrike(canvasX, canvasY) {
                var _a, _b;
                const paneOiData = (_a = oiDataMap.get(paneId)) !== null && _a !== void 0 ? _a : [];
                if (paneOiData.length === 0)
                    return null;
                const paneRange = (_b = priceRangeMap.get(paneId)) !== null && _b !== void 0 ? _b : null;
                const allPrices = paneOiData.map((d) => d.price);
                const minP = paneRange ? Math.min(paneRange.from, paneRange.to) : Math.min(...allPrices);
                const maxP = paneRange ? Math.max(paneRange.from, paneRange.to) : Math.max(...allPrices);
                const W = canvas.width;
                const H = canvas.height;
                const maxOI = Math.max(...paneOiData.map((d) => Math.max(d.call, d.put)));
                if (maxOI === 0)
                    return null;
                const axisX = W;
                const maxBarW = W - 4;
                // Use same dynamic bar height as drawOIProfile for accurate hit detection
                const dynBarH = computeBarHeight(paneOiData, paneRange, H);
                for (const d of paneOiData) {
                    const cy = priceToY(d.price, minP, maxP, H);
                    // CALL bar is drawn ABOVE center: [cy - dynBarH, cy)
                    if (canvasY >= cy - dynBarH && canvasY < cy) {
                        const callW = (d.call / maxOI) * maxBarW;
                        if (canvasX >= axisX - callW && canvasX <= axisX)
                            return d;
                    }
                    // PUT bar is drawn BELOW center: [cy, cy + dynBarH]
                    if (canvasY >= cy && canvasY <= cy + dynBarH) {
                        const putW = (d.put / maxOI) * maxBarW;
                        if (canvasX >= axisX - putW && canvasX <= axisX)
                            return d;
                    }
                }
                return null;
            }
            // Track the currently hovered strike and last mouse position for live tooltip refresh
            let currentHoveredStrike = null;
            let lastTooltipClientX = 0;
            let lastTooltipClientY = 0;
            let lastOiPanelLeftVp = 0;
            let lastBarLeftVp = 0; // viewport X of the left edge of the hovered bar
            const showTooltip = (strike, clientX, clientY, oiPanelLeftVp, barLeftVp) => {
                var _a, _b, _c, _d, _e, _f;
                currentHoveredStrike = strike;
                lastTooltipClientX = clientX;
                lastTooltipClientY = clientY;
                if (oiPanelLeftVp !== undefined)
                    lastOiPanelLeftVp = oiPanelLeftVp;
                if (barLeftVp !== undefined)
                    lastBarLeftVp = barLeftVp;
                // Apply theme-aware styles each time the tooltip is shown
                const isLight = getTheme() === "light" || getTheme() === "Light";
                Object.assign(oiTooltip.style, {
                    background: isLight ? "#ffffff" : "#1e1e2e",
                    border: isLight ? "1px solid #d1d4dc" : "1px solid #555",
                    color: isLight ? "#131722" : "#eee",
                    boxShadow: isLight ? "0 4px 16px rgba(0,0,0,0.15)" : "0 4px 16px rgba(0,0,0,0.5)",
                });
                const ceItemJson = strike.ceItem ? JSON.stringify(strike.ceItem).replace(/"/g, "&quot;") : "";
                const peItemJson = strike.peItem ? JSON.stringify(strike.peItem).replace(/"/g, "&quot;") : "";
                const callCr = formatOI(strike.call);
                const putCr = formatOI(strike.put);
                const textColor = isLight ? "#131722" : "#eee";
                const subTextColor = isLight ? "#555" : "#aaa";
                const borderColor = isLight ? "#d1d4dc" : "#444";
                // OI Change = current OpenInterest − Prev_OI
                const callPrevOI = (_b = (_a = strike.ceItem) === null || _a === void 0 ? void 0 : _a.Prev_OI) !== null && _b !== void 0 ? _b : 0;
                const putPrevOI = (_d = (_c = strike.peItem) === null || _c === void 0 ? void 0 : _c.Prev_OI) !== null && _d !== void 0 ? _d : 0;
                const callChange = strike.call - callPrevOI;
                const putChange = strike.put - putPrevOI;
                const callChangeFmt = (callChange >= 0 ? "+" : "") + formatOI(Math.abs(callChange));
                const putChangeFmt = (putChange >= 0 ? "+" : "") + formatOI(Math.abs(putChange));
                oiTooltip.innerHTML = `
          <div style="font-weight:800;font-size:12px;margin-bottom:2px;color:${textColor};">
            Strike: ${strike.price} OI
          </div>
          <div style="font-size:12px;margin-bottom:4px;color:${subTextColor};border-bottom:1px solid ${borderColor};padding-bottom:4px;">
            Expiry: ${strike.expiry || "—"}
          </div>
          <div style="margin-bottom:2px;display:flex;align-items:center;justify-content:space-between;gap:8px;">
            <span style="display:flex;align-items:center;gap:5px;">
              <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:#D16777;flex-shrink:0;"></span>
              <span style="color:${textColor};font-weight:600;font-size:12px;">Call OI</span>
              <span style="color:${textColor};font-size:12px;">${callCr}</span>
            </span>
            <span style="display:flex;gap:3px;">
              <button class="oi-buy" data-action="buy-call" data-item="${ceItemJson}">B</button>
              <button class="oi-sell" data-action="sell-call" data-item="${ceItemJson}">S</button>
            </span>
          </div>
          <div style="margin-bottom:4px;padding-left:15px;font-size:11px;color:${textColor};">
            Call OI Change: ${callChange >= 0 ? callChangeFmt : "−" + formatOI(Math.abs(callChange))}
          </div>
          <div style="border-top:1px solid ${borderColor};margin-bottom:4px;"></div>
          <div style="margin-bottom:2px;display:flex;align-items:center;justify-content:space-between;gap:8px;">
            <span style="display:flex;align-items:center;gap:5px;">
              <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:#34ADA0;flex-shrink:0;"></span>
              <span style="color:${textColor};font-weight:600;font-size:12px;">Put OI</span>
              <span style="color:${textColor};font-size:12px;">${putCr}</span>
            </span>
            <span style="display:flex;gap:3px;">
              <button class="oi-buy" data-action="buy-put" data-item="${peItemJson}">B</button>
              <button class="oi-sell" data-action="sell-put" data-item="${peItemJson}">S</button>
            </span>
          </div>
          <div style="padding-left:15px;font-size:11px;color:${textColor};">
            Put OI Change: ${putChange >= 0 ? putChangeFmt : "−" + formatOI(Math.abs(putChange))}
          </div>
        `;
                oiTooltip.style.visibility = "hidden";
                oiTooltip.style.display = "block";
                const tooltipW = oiTooltip.offsetWidth || 220;
                const tooltipH = oiTooltip.offsetHeight || 140;
                const vpH = (_f = (_e = iframe.contentWindow) === null || _e === void 0 ? void 0 : _e.innerHeight) !== null && _f !== void 0 ? _f : window.innerHeight;
                const MARGIN = 8;
                // ── Horizontal: fixed to the LEFT edge of the hovered OI bar ──
                // lastBarLeftVp is the viewport X of the left edge of the widest bar for the hovered strike.
                // Falls back to panel left, then cursor if not yet set.
                const barLeft = lastBarLeftVp > 0 ? lastBarLeftVp : (lastOiPanelLeftVp > 0 ? lastOiPanelLeftVp : clientX);
                let tx = barLeft - tooltipW - 8;
                if (tx < MARGIN)
                    tx = MARGIN;
                // ── Vertical: centered at cursor Y ──
                let ty = clientY - tooltipH / 2;
                if (ty + tooltipH > vpH - MARGIN)
                    ty = vpH - tooltipH - MARGIN;
                if (ty < MARGIN)
                    ty = MARGIN;
                oiTooltip.style.left = `${tx}px`;
                oiTooltip.style.top = `${ty}px`;
                oiTooltip.style.visibility = "visible";
            };
            guiWrapper.addEventListener("mousemove", (e) => {
                var _a;
                if (!getOiProfileEnabled())
                    return;
                const wrapperRect = guiWrapper.getBoundingClientRect();
                const relX = e.clientX - wrapperRect.left;
                const oiPanelStartX = wrapperRect.width * (1 - OI_PANEL_WIDTH_RATIO);
                if (relX < oiPanelStartX) {
                    scheduleTooltipHide();
                    return;
                }
                const canvasX = relX - oiPanelStartX;
                const canvasY = e.clientY - wrapperRect.top;
                const strike = getHoveredStrike(canvasX, canvasY);
                if (strike) {
                    cancelTooltipHide();
                    const oiPanelLeftVp = wrapperRect.left + oiPanelStartX;
                    const paneOiData = (_a = oiDataMap.get(paneId)) !== null && _a !== void 0 ? _a : [];
                    const maxOI = Math.max(...paneOiData.map((d) => Math.max(d.call, d.put)));
                    const maxBarW = canvas.width - 4;
                    let barLeftVp = oiPanelLeftVp; // fallback: left edge of panel
                    if (maxOI > 0) {
                        const callW = (strike.call / maxOI) * maxBarW;
                        const putW = (strike.put / maxOI) * maxBarW;
                        const widerBarW = Math.max(callW, putW);
                        // Left edge of bar in canvas coords = canvas.width - widerBarW
                        barLeftVp = oiPanelLeftVp + (canvas.width - widerBarW);
                    }
                    showTooltip(strike, e.clientX, e.clientY, oiPanelLeftVp, barLeftVp);
                }
                else {
                    scheduleTooltipHide();
                }
            });
            guiWrapper.addEventListener("mouseleave", () => {
                scheduleTooltipHide();
                currentHoveredStrike = null;
            });
            // Live tooltip refresh — re-render tooltip content every 500ms while it is visible.
            // Since oiDataMap entries are mutated in-place by handleOiWebsocketUpdate, the
            // currentHoveredStrike reference always reflects the latest OI values.
            setInterval(() => {
                if (currentHoveredStrike &&
                    oiTooltip &&
                    oiTooltip.style.display !== "none") {
                    showTooltip(currentHoveredStrike, lastTooltipClientX, lastTooltipClientY);
                }
            }, 500);
            let lastPaneFrom = null;
            let lastPaneTo = null;
            setInterval(() => {
                try {
                    // Use paneId (= stable TV chart index) directly — not DOM position
                    const r = tvWidget.chart(paneId).getPanes()[0].getMainSourcePriceScale().getVisiblePriceRange();
                    if (r && (r.from !== lastPaneFrom || r.to !== lastPaneTo)) {
                        lastPaneFrom = r.from;
                        lastPaneTo = r.to;
                        priceRangeMap.set(paneId, { from: r.from, to: r.to });
                    }
                }
                catch (_) { /* ignore */ }
            }, 100);
            const iframeWin = iframe.contentWindow;
            const loop = () => {
                var _a, _b;
                if (getOiProfileEnabled()) {
                    const paneOiData = (_a = oiDataMap.get(paneId)) !== null && _a !== void 0 ? _a : [];
                    const paneRange = (_b = priceRangeMap.get(paneId)) !== null && _b !== void 0 ? _b : null;
                    drawOIProfile(ctx, canvas, paneOiData, paneRange);
                }
                else {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                }
                iframeWin.requestAnimationFrame(loop);
            };
            loop();
            const ro = new ResizeObserver(() => setCanvasSize());
            ro.observe(guiWrapper);
        });
    }, 500);
}
